import React, { useMemo } from 'react';
import { Collapse, Row, Col, Tooltip } from 'antd';
import { Info } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import styled from 'styled-components';
import { CaretDownOutlined } from '@ant-design/icons';
import { v4 } from 'uuid';

import SentryWrap from 'components/Sentry';
import AudioReader from 'components/audio/audioReader';
import AIUsageInfo from 'components/Modals/AiInfoModal';
import { useAudioChecker } from 'store/audio/selectors';
import COLOR from 'utils/constants/color';
import { AIfeebackImage, AiFeedbackImage } from 'utils/assets/images';
import { useGradeBook } from './hooks/gradeBook/useGradeBook';

const { Panel } = Collapse;

const AIContent = styled.div`
  text-align: left;
  width: 100%;
  border-radius: 20px;
  padding: 25px 25px 20px 25px;
  background: rgb(241, 235, 251);
  font-size: 13px;
  font-weight: 500;
  margin-top: 10px;
`;

const ErrorAiArea = styled.div<{ isFeedBack?: boolean }>`
  background: #0ea5e9;
  border-radius: 10px;
  position: relative;
  color: ${COLOR.white};
  width: 245px;
  padding-left: 10px;
  padding-top: ${(p) => (p.isFeedBack ? '3px' : '1px')};
  font-size: ${(p) => (p.isFeedBack ? '11px' : '14px')};
  display: flex;
  align-items: center;
`;

const GradeAiArea = styled.div<{ isFeedBack?: boolean }>`
  background: #1E3A8A;
  border-radius: 10px;
  position: relative;
  color: ${COLOR.white};
  width: 240px;
  padding-left: 10px;
  padding-top: ${(p) => (p.isFeedBack ? '3px' : '1px')};
  font-size: ${(p) => (p.isFeedBack ? '11px !important' : '14px')};
  display: flex;
  align-items: center;
`;

const AllAiSection = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 0px;
`;

const AIIcon = styled.img<{ isFeedBack?: boolean }>`
  position: absolute;
  height: 50px;
  width: 45px;
  top: -20px;
  right: ${(p) => (p.isFeedBack ? '-9px' : '-15px')};
`;

const AIIcons = styled.img`
  position: absolute;
  height: 45px;
  width: 38px;
  top: -18px;
  right: -12px;
`;

const ErrorAiAreas = styled.div`
  background: #38b54b;
  border-radius: 10px;
  position: relative;
  color: ${COLOR.white};
  width: 230px;
  padding-left: 10px;
  padding-top: 1px;
  margin-right: 2px;
  display: flex;
  align-items: center;
`;

const AITextSection = styled.span<{ isFeedBack?: boolean }>`
  font-size: ${(p) => (p.isFeedBack ? '11px' : '14px')};
`;

const CustomCaretDownOutlined = styled(CaretDownOutlined)<{ isActive?: boolean }>`
  font-size: 11px;
  margin-left: 6px;
  transform: ${(props) => (props.isActive ? 'rotate(180deg)' : 'rotate(0deg)')};
  color: #fff !important;
`;

const InnerAccordionWrap = styled.div`
  .ant-collapse-item:not(:first-child) {
    margin-top: 16px;
  }
`;

const RubricsCapsule = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 14px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.55);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
`;

const RubricsHintText = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;
  max-width: 100%;
`;

const RUBRICS_TOGGLE_HINT_ES =
  'Por favor, active el interruptor de rúbricas para ver la retroalimentación.';

const RubricsHintBodyText = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #4c1d95;
  line-height: 1.45;
`;

export interface AiGradeData {
  type?: string;
  ai_grade_point?: number;
  ai_grade_feedback?: string;
  max_mark_per_question?: number;
}

interface AIGradingFeedbackProps {
  ai_grade: AiGradeData | null;
  languageData: string;
  id: string;
  isFeedBack?: boolean;
  hasRubricsToggle?: boolean;
}

interface AIFeedbackProps {
  ai_feedback: string | null;
  ai_suggestion: string | null;
  id: string;
  languageData: string;
  isFeedBack?: boolean;
}

function hasMeaningfulAiDisplayString(value: string | null | undefined): boolean {
  if (value == null || typeof value !== 'string') return false;
  const t = value.trim();
  if (t === '') return false;
  const lower = t.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return false;
  return true;
}

function resolveAiErrorsBodyHtml(
  ai_feedback: string | null | undefined,
  ai_grade_feedback: string | null | undefined
): string | null {
  if (hasMeaningfulAiDisplayString(ai_feedback)) return ai_feedback as string;
  if (hasMeaningfulAiDisplayString(ai_grade_feedback)) return ai_grade_feedback as string;
  return null;
}

function AIGradingFeedback({
  ai_grade,
  languageData,
  id,
  isFeedBack,
  hasRubricsToggle = false,
}: AIGradingFeedbackProps): React.ReactElement | null {
  const audioChecker = useAudioChecker();
  if (!ai_grade) return null;
  const { type, ai_grade_feedback } = ai_grade;
  if (type === 'rubrics' && !hasRubricsToggle) return null;
  if (type === 'rubrics' && hasRubricsToggle) {
    return (
      <SentryWrap>
        <AIContent>
          <RubricsHintBodyText>{RUBRICS_TOGGLE_HINT_ES}</RubricsHintBodyText>
        </AIContent>
      </SentryWrap>
    );
  }
  return (
    <SentryWrap>
      <div style={{ position: 'relative' }} className="gradefeed_area">
        <div style={{ width: '100%', justifyContent: 'flex-end' }}>
          <div style={{ position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '20px 0px',
              }}
            >
              <GradeAiArea isFeedBack={isFeedBack}>
                Explicación del grado de IA
                <AIUsageInfo styles={{ icon: { fontSize: 16, color: 'white' } }} />{' '}
                <AIIcon isFeedBack={isFeedBack} src={AIfeebackImage} />
              </GradeAiArea>
              <AudioReader
                text={
                  hasRubricsToggle
                    ? ''
                    : hasMeaningfulAiDisplayString(ai_grade_feedback as string | undefined)
                      ? (ai_grade_feedback as string)
                      : ''
                }
                audioChecker={audioChecker}
                id={id}
                language={languageData}
                styles={{ width: 90 }}
              />
            </div>
          </div>
          <AIContent>
            {hasRubricsToggle ? (
              <RubricsHintBodyText>{RUBRICS_TOGGLE_HINT_ES}</RubricsHintBodyText>
            ) : hasMeaningfulAiDisplayString(ai_grade_feedback as string | undefined) ? (
              ai_grade_feedback
            ) : (
              'Waiting for a grade...'
            )}
          </AIContent>
        </div>
      </div>
    </SentryWrap>
  );
}

function insertSpaceAfterPunctuationBeforeNewSentence(text: string): string {
  return text.replace(/([.!?])(\p{Lu})/gu, '$1 $2');
}

function stripTags(input: string | null | undefined): string {
  if (!input) return '';
  let strippedInput = input.replace(/<[^>]*>?/gm, '');
  strippedInput = strippedInput.replace(/```html|```|\*\*/g, '');
  return insertSpaceAfterPunctuationBeforeNewSentence(strippedInput);
}

function AIErrorsSection({
  ai_feedback,
  isFeedBack,
  ai_grade,
}: Pick<AIFeedbackProps, 'ai_feedback' | 'isFeedBack'> & {
  ai_grade: AiGradeData | null;
}): React.ReactElement | null {
  const bodyHtml = resolveAiErrorsBodyHtml(ai_feedback, ai_grade?.ai_grade_feedback);
  if (!bodyHtml) return null;
  return (
    <div style={{ position: 'relative' }}>
      <Row>
        <Col md={24} sm={24} lg={21}>
          <AllAiSection>
            <ErrorAiArea isFeedBack={isFeedBack}>
              Errores y cambios necesarios
              <AIUsageInfo styles={{ icon: { fontSize: 16, color: 'white' } }} />
              <AIIcon isFeedBack={isFeedBack} src={AIfeebackImage} />
            </ErrorAiArea>
            <div style={{ marginLeft: 10, cursor: 'help' }}>
              <Tooltip
                placement={'topLeft'}
                trigger={'hover'}
                title={
                  'If any questions arise or errors are detected, please ensure that you review them carefully and make the necessary corrections.'
                }
                overlayStyle={{ zIndex: 2147483647 }}
              >
                <Info />
              </Tooltip>
            </div>
          </AllAiSection>
        </Col>
      </Row>
      <AIContent dangerouslySetInnerHTML={{ __html: bodyHtml }} />
    </div>
  );
}

function AIConsejosSection({
  ai_suggestion,
  id,
  languageData,
  isFeedBack,
}: Pick<AIFeedbackProps, 'ai_suggestion' | 'id' | 'languageData' | 'isFeedBack'>): React.ReactElement | null {
  const text = 'Consejos para mejorar';
  const maxLength = 12;
  const isMobile = useMediaQuery('(max-width: 1250px)');
  const audioChecker = useAudioChecker();

  if (!hasMeaningfulAiDisplayString(ai_suggestion)) return null;
  return (
    <div style={{ position: 'relative' }}>
      <Row>
        <Col md={24} sm={24} xs={18} lg={18}>
          <AllAiSection>
            <ErrorAiAreas>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {text.length > maxLength ? (
                  isMobile ? (
                    <Tooltip title={text}>
                      <span>{text.slice(0, maxLength) + '...'}</span>
                    </Tooltip>
                  ) : (
                    <AITextSection isFeedBack={isFeedBack}>{text}</AITextSection>
                  )
                ) : (
                  <AITextSection isFeedBack={isFeedBack}>{text}</AITextSection>
                )}
                <AIUsageInfo styles={{ icon: { fontSize: 16, color: 'white' } }} />
                <AIIcons src={AiFeedbackImage} />
              </div>
            </ErrorAiAreas>
            <div style={{ marginLeft: 10, cursor: 'help', zIndex: 99 }}>
              <Tooltip
                placement={'topLeft'}
                trigger={'hover'}
                title={
                  'Should any questions arise or errors be detected, please, ask your teacher for feedback.'
                }
                overlayStyle={{ zIndex: 2147483647 }}
              >
                <Info />
              </Tooltip>
            </div>
          </AllAiSection>
        </Col>
        <Col md={24} sm={24} xs={6} lg={6}>
          <div
            className="feedBackSound"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              margin: '10px 0',
              width: '100%',
            }}
          >
            <AudioReader
              text={stripTags(ai_suggestion)}
              audioChecker={audioChecker}
              id={id}
              language={languageData}
            />
          </div>
        </Col>
      </Row>
      <AIContent dangerouslySetInnerHTML={{ __html: stripTags(ai_suggestion) }} />
    </div>
  );
}

export interface AIGradingAndFeedbackProps {
  ai_grade: AiGradeData | null;
  ai_feedback: string | null;
  ai_suggestion: string | null;
  languageData: string;
  isFeedBack?: boolean;
  isSuggestion?: boolean;
  question: any;
  rubricsToggle?: React.ReactNode;
  isCompleted?: boolean | null;
}

export default function AIGradingAndFeedback({
  ai_grade,
  ai_feedback,
  ai_suggestion,
  languageData,
  isFeedBack = false,
  isSuggestion = true,
  question = {},
  rubricsToggle,
  isCompleted
}: AIGradingAndFeedbackProps): React.ReactElement {
  const gradingId = v4();
  const feedbackId = v4();
  const [mainActiveKey, setMainActiveKey] = React.useState<string | string[]>(['1']);
  const hasRubricsToggle = Boolean(rubricsToggle);
  const { typeCheckQuestion } = useGradeBook();
  const validAiFeedback = useMemo(() => {
    const isItValid = !typeCheckQuestion(question?.type, true) && (ai_feedback === null || ai_feedback === undefined);
    return !isCompleted ? false : isItValid; 
  }, [ai_feedback, isCompleted]);

  const validAiSuggestion = useMemo(() => {
    const isItValid = !typeCheckQuestion(question?.type, true) && (ai_suggestion === null || ai_suggestion === undefined);
    return !isCompleted ? false : isItValid; 
  }, [ai_suggestion, isCompleted]);

  return (
    <>
      <Collapse
        className="gradefeedback"
        expandIcon={({ isActive }) => <CustomCaretDownOutlined isActive={isActive} />}
        bordered={false}
        style={{ background: 'none', width: '100%' }}
        activeKey={mainActiveKey}
        onChange={setMainActiveKey}
      >
        <Panel
          header={
            <span
              style={{
                fontWeight: 'bold',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 6,
                width: '100%',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                Calificado por IA:{' '}
                {validAiFeedback ? (
                  <>
                    -
                    <span className="flex items-center gap-2" style={{ fontWeight: 600 }}>
                      (Processing...)
                    </span>
                  </>
                ) : hasRubricsToggle ? (
                  <RubricsCapsule>Rúbricas</RubricsCapsule>
                ) : (
                  <>
                    {ai_grade?.ai_grade_point} / {ai_grade?.max_mark_per_question}
                  </>
                )}
              </span>
            </span>
          }
          key="1"
        >
          {isSuggestion && (
            <AIErrorsSection ai_feedback={ai_feedback} isFeedBack={isFeedBack} ai_grade={ai_grade} />
          )}
          <InnerAccordionWrap>
          <Collapse
            expandIcon={({ isActive }) => <CustomCaretDownOutlined isActive={isActive} />}
            bordered={false}
            style={{ background: 'none', width: '100%', marginTop: 16 }}
            defaultActiveKey={[]}
          >
            <Panel
              header={
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 6,
                    width: '100%',
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#fff' }}>Explicación del grado de IA</span>
                </span>
              }
              key="explicacion"
            >
              <AIGradingFeedback
                ai_grade={ai_grade}
                languageData={languageData}
                id={gradingId}
                isFeedBack={isFeedBack}
                hasRubricsToggle={hasRubricsToggle}
              />
            </Panel>
            {isSuggestion && hasMeaningfulAiDisplayString(ai_suggestion) && (
              <Panel
                header={
                  <span style={{ fontWeight: 600, color: '#fff' }}>
                    Consejos para mejorar {validAiSuggestion ? <span style={{ fontSize: 12, color: 'yellow' }}>(Processing...)</span> : ""}
                  </span>
                }
                key="consejos"
              >
                <AIConsejosSection
                  ai_suggestion={ai_suggestion}
                  id={feedbackId}
                  languageData={languageData}
                  isFeedBack={isFeedBack}
                />
              </Panel>
            )}
          </Collapse>
          </InnerAccordionWrap>
        </Panel>
      </Collapse>
    </>
  );
}
